import type { ReactElement } from 'react';
import { useCallback, useState } from 'react';
import { Box, Popover, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import World from '@svg-maps/world';
import { CircleFlag } from 'react-circle-flags';
import { SVGMap } from 'react-svg-map';
import type {
  MarkdownRemarkFrontmatterCopy,
  MarkdownRemarkFrontmatterGlobalPayoutData,
} from '../../../generated/types';
import 'react-svg-map/lib/index.css';
import { GlobalPayoutTable, type PpGlobalPayoutTable } from './GlobalPayoutTable';

type PpGlobalPayoutMap = {
  availableCountries: Array<string | null | undefined>;
  copy: MarkdownRemarkFrontmatterCopy;
  data: MarkdownRemarkFrontmatterGlobalPayoutData[];
};

type TpPopoverState = {
  anchorEl: SVGElement | null;
  open: boolean;
};

type TpSelectedCountry =
  | (Omit<PpGlobalPayoutTable, 'copy'> & {
      countryCode: string | null;
      countryName: string;
    })
  | undefined;

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 8),
  width: '100%',
  '& .svg-map__location': {
    fill: '#D3DADF',
    stroke: theme.palette.websiteBrand.greyscale.mediumGrey,
    '&:hover': {
      fill: '#F3F4FF',
    },
    '&.available': {
      fill: '#5C5B62',
    },
  },
}));

const StyledPopover = styled(Popover)(({ theme }) => ({
  pointerEvents: 'none',
  '& .MuiPaper-root': {
    border: `1px solid ${theme.palette.websiteBrand.greyscale.grey}`,
    borderRadius: theme.borderRadius.sm,
    boxShadow: '0px 4px 25px 0px rgba(0, 0, 0, 0.2)',
    padding: theme.spacing(2),
  },
  '& .MuiPopover-paper': {
    overflow: 'visible',
    '&::before': {
      border: '8px solid transparent',
      borderTopColor: theme.palette.background.paper,
      content: '""',
      left: '50%',
      marginLeft: theme.spacing(-1),
      position: 'absolute',
      top: '100%',
    },
  },
}));

export function GlobalPayoutMap({
  availableCountries,
  copy,
  data,
}: PpGlobalPayoutMap): ReactElement {
  const [selectedCountry, setSelectedCountry] = useState<TpSelectedCountry>(undefined);
  const [popoverState, setPopoverState] = useState<TpPopoverState>({
    anchorEl: null,
    open: false,
  });

  const handleLocationMouseOver = useCallback(
    (event: React.MouseEvent<SVGElement>) => {
      const target = event.target as SVGElement;
      const locationName = target.getAttribute('name');
      const locationCode = target.getAttribute('id');
      if (!locationName) {
        return;
      }
      const currentCountry = data.find((item) => item.country === locationName);
      setSelectedCountry({
        countryCode: locationCode,
        countryData: currentCountry,
        countryName: locationName,
      });
      setPopoverState({
        anchorEl: target,
        open: true,
      });
    },
    [data],
  );

  const handleLocationMouseOut = useCallback(() => {
    setPopoverState((prev) => ({ ...prev, open: false }));
  }, []);

  const getLocationClassName = useCallback(
    (location: { name: string }) => {
      const chosenCountry = availableCountries.find((country) => country === location.name);
      return `svg-map__location${chosenCountry ? ' available' : ''}`;
    },
    [availableCountries],
  );

  return (
    <StyledBox>
      <SVGMap
        locationClassName={getLocationClassName}
        map={World}
        onLocationClick={handleLocationMouseOver}
        onLocationMouseOut={handleLocationMouseOut}
      />
      <StyledPopover
        disableRestoreFocus
        anchorEl={popoverState.anchorEl}
        anchorOrigin={{
          horizontal: 'center',
          vertical: 'center',
        }}
        elevation={2}
        open={popoverState.open}
        transformOrigin={{
          horizontal: 'center',
          vertical: 'bottom',
        }}
        onClose={handleLocationMouseOut}
      >
        <Stack alignItems="flex-start" spacing={2}>
          <Stack alignItems="center" direction="row" justifyContent="center" spacing={1}>
            {selectedCountry?.countryCode && (
              <CircleFlag countryCode={selectedCountry.countryCode} height={24} width={24} />
            )}
            <Typography gutterBottom textAlign="center" variant="paragraphBodyLBold">
              {selectedCountry?.countryName}
            </Typography>
          </Stack>
          <GlobalPayoutTable copy={copy} countryData={selectedCountry?.countryData} />
        </Stack>
      </StyledPopover>
    </StyledBox>
  );
}
